import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { DbLead, LeadStatus } from "@/lib/types";

export interface PaginatedLeadsParams {
  page: number;
  perPage: number;
  search: string;
  filterScore: string;
  filterStatus: string;
  filterAssignee: string;
  sortField: string;
  sortDir: "asc" | "desc";
  /** Inclusive ISO-8601 bounds on created_at, e.g. for the Reports page's date-range filter. */
  dateFrom?: string;
  dateTo?: string;
}

/** Applies the Leads list's filters to a Supabase query builder. Shared between
 *  the paginated fetch and the "export all matching rows" CSV path so the two
 *  can never drift out of sync. */
export function applyLeadFilters<T>(query: T, params: Pick<PaginatedLeadsParams, "search" | "filterScore" | "filterStatus" | "filterAssignee" | "dateFrom" | "dateTo">): T {
  const { search, filterScore, filterStatus, filterAssignee, dateFrom, dateTo } = params;
  let q = query as any;

  const term = search.trim().replace(/[%,()]/g, "");
  if (term) {
    q = q.or(`name.ilike.%${term}%,zip_code.ilike.%${term}%,acuity_notes.ilike.%${term}%`);
  }

  if (filterScore === "100") q = q.eq("score", 100);
  else if (filterScore === "hot") q = q.gte("score", 85).lt("score", 100);
  else if (filterScore === "warm") q = q.gte("score", 50).lt("score", 85);
  else if (filterScore === "cold") q = q.lt("score", 50);

  if (filterStatus !== "all") q = q.eq("status", filterStatus as LeadStatus);

  if (filterAssignee === "unassigned") q = q.is("assigned_to", null);
  else if (filterAssignee !== "all") q = q.eq("assigned_to", filterAssignee);

  if (dateFrom) q = q.gte("created_at", dateFrom);
  if (dateTo) q = q.lte("created_at", dateTo);

  return q as T;
}

/** Server-side paginated + filtered + sorted leads for the Leads list page.
 *  Only the current page's rows are ever pulled into memory. */
export function usePaginatedLeads(params: PaginatedLeadsParams) {
  const { user } = useAuth();
  // Depend on the user's id, not the whole `user` object — Supabase issues a
  // new session/user object on every token refresh (e.g. right after the tab
  // regains focus) even though it's the same signed-in user. Depending on
  // the object reference would re-trigger `loading` (blanking the table) on
  // every refresh.
  const userId = user?.id;
  const [leads, setLeads] = useState<DbLead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const requestId = useRef(0);
  // Only the very first fetch should show the full-page spinner — a refetch
  // after e.g. syncing a calendar event shouldn't blank the whole list out
  // from under the user while it silently reloads in the background.
  const hasLoadedOnce = useRef(false);

  const { page, perPage, search, filterScore, filterStatus, filterAssignee, sortField, sortDir, dateFrom, dateTo } = params;

  const fetchPage = useCallback(async () => {
    if (!userId) { setLeads([]); setTotalCount(0); setLoading(false); return; }
    const id = ++requestId.current;
    if (!hasLoadedOnce.current) setLoading(true);

    let query = supabase.from("leads").select("*", { count: "exact" });
    query = applyLeadFilters(query, { search, filterScore, filterStatus, filterAssignee, dateFrom, dateTo });
    query = query
      .order(sortField, { ascending: sortDir === "asc" })
      .range(page * perPage, page * perPage + perPage - 1) as any;

    const { data, count, error } = await query;
    // A newer request already landed — drop this stale response.
    if (id !== requestId.current) return;
    if (!error) {
      setLeads((data as DbLead[]) ?? []);
      setTotalCount(count ?? 0);
    }
    setLoading(false);
    hasLoadedOnce.current = true;
  }, [userId, page, perPage, search, filterScore, filterStatus, filterAssignee, sortField, sortDir, dateFrom, dateTo]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  return { leads, totalCount, loading, refetch: fetchPage };
}
